import { z } from 'zod';

import { logError } from '~/common/utils/log';

export const PROMOTED_CONTESTS_COLLECTION_SLUGS = [
  'featured',
  'live',
  'homepage',
  'discover',
] as const;

export type PromotedContestsCollectionSlug = (typeof PROMOTED_CONTESTS_COLLECTION_SLUGS)[number];

const displayString = z.string().optional().catch(undefined);

const tabSchema = z.object({
  value: z.string(),
  title: z.string(),
  contest_ids: z.array(z.string()).min(1),
});

const viewMoreSchema = z
  .object({
    leagueAlias: displayString,
    leagueLabel: displayString,
    contestType: displayString,
    contestTypeLabel: displayString,
  })
  .optional()
  .catch(undefined);

const carouselSchema = z.object({
  id: z.string(),
  slug: displayString,
  title: z.string(),
  iconUrl: displayString,
  isLive: z.boolean().catch(false),
  showPrivate: z.boolean(),
  showFull: z.boolean(),
  showInProgress: z.boolean(),
  filterType: z.enum(['league', 'type']).nullable(),
  viewMore: viewMoreSchema,
  tabs: z.array(tabSchema).min(1),
});

const collectionEnvelopeSchema = z.object({
  id: z.string().optional(),
  data: z.array(z.unknown()),
});

export type PromotedContestTab = z.infer<typeof tabSchema>;

export type PromotedContestCarousel = z.infer<typeof carouselSchema>;

export type ContractReport = (message: string, context: Record<string, unknown>) => void;

const readCarouselId = (item: unknown): string | undefined => {
  if (typeof item !== 'object' || item === null || !('id' in item)) {
    return undefined;
  }
  return typeof item.id === 'string' ? item.id : undefined;
};

export const parsePromotedContestsCollection = (
  raw: unknown,
  report: ContractReport = logError
): PromotedContestCarousel[] => {
  const envelope = collectionEnvelopeSchema.safeParse(raw);

  if (envelope.success === false) {
    report('Unexpected promoted-contests collection response shape', {
      issues: envelope.error.issues.map((issue) => issue.path.join('.')),
    });
    throw new Error('Unexpected promoted-contests collection response');
  }

  return envelope.data.data.flatMap((item) => {
    const carousel = carouselSchema.safeParse(item);

    if (carousel.success === false) {
      report('Dropped invalid promoted-contests carousel', {
        carouselId: readCarouselId(item),
        issues: carousel.error.issues.map((issue) => issue.path.join('.')),
      });
      return [];
    }

    return [carousel.data];
  });
};
